import { LocationPoint, Measurement } from '@/types';

export const POSITION_ERROR_MESSAGE = 'Unable to determine current location. Please enter an address.';

export const USA_CENTER_COORDINATES = {
  latitude: 44.967244,
  longitude: -103.771555,
};

function convertMeasurements(distance: number, measurement: Measurement = 'mi'): number {
  switch (measurement.toLowerCase()) {
    case 'mi':
      return parseFloat((distance * 0.62137).toFixed(1));
    case 'm':
      return parseFloat((distance * 1000).toFixed());
    case 'km':
    default:
      return parseFloat(distance.toFixed(1));
  }
}

function deg2rad(deg: number): number {
  return deg * (Math.PI / 180);
}

function haversine(from: LocationPoint, to: LocationPoint): number {
  const lat1: number = deg2rad(from.latitude);
  const lon1: number = deg2rad(from.longitude);
  const lat2: number = deg2rad(to.latitude);
  const lon2: number = deg2rad(to.longitude);

  const R = 6372.8;
  const dLat: number = lat2 - lat1;
  const dLon: number = lon2 - lon1;

  const a: number = Math.sin(dLat / 2)
    * Math.sin(dLat / 2)
    + Math.sin(dLon / 2)
    * Math.sin(dLon / 2)
    * Math.cos(lat1)
    * Math.cos(lat2);

  const c: number = 2 * Math.asin(Math.sqrt(a));

  return R * c;
}

export function getGeolocationPosition(options?: PositionOptions): Promise<Position> {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: Position) => {
          resolve(position);
        },
        () => {
          reject(new Error(POSITION_ERROR_MESSAGE));
        },
        options,
      );
    } else {
      reject(new Error(POSITION_ERROR_MESSAGE));
    }
  });
}

export function getDistanceBetween(from: LocationPoint, to: LocationPoint, measurement?: Measurement): number {
  const distance: number = haversine(from, to);
  return convertMeasurements(distance, measurement);
}

export async function getGeolocationPermission(): Promise<PermissionStatus> {
  return navigator.permissions.query({
    name: 'geolocation',
  });
}
